/**
 * Tocar una palabra DENTRO de la ficha (encargo 49): la definición, el ejemplo
 * y su traducción dejan de ser texto quieto. El toque pregunta el significado
 * de esa palabra en esa oración (`POST /lexicon/meaning`) y lo muestra debajo
 * del recuadro, con la opción de abrir su ficha — que se apila sobre ésta, con
 * «Volver», hasta {@link MAX_SHEET_DEPTH} fichas.
 *
 * Lo que se muestra es la MISMA tarjeta «Con ejemplo» que al tocar una palabra
 * en la charla (`WordPopover` + `MeaningBody`, encargos 64 y 66), flotando
 * sobre la ficha y anclada a la palabra tocada como en la charla: chips,
 * Escuchar, Guardar y «Ver ficha».
 *
 * Flota sobre toda la pantalla (encargo 526), no adentro del scroll de la
 * ficha: abrirla no estira la hoja ni la scrollea. Va abajo de la palabra si
 * entra, si no arriba, y sigue a la palabra si la ficha se scrollea.
 *
 * La traducción del ejemplo está en español: tocar «dirigir» no finge que es
 * una palabra inglesa (lo dice el backend, `not-english`).
 *
 * Vive aparte de `WordSheetLib.tsx` a propósito: la ficha sólo enchufa
 * `TapText` y `useSheetTap`, y sin `tapLookup` se dibuja exactamente como hoy.
 */
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { ContextMeaningOrigin, ContextMeaningPublicConfig } from '@orborix/shared-types';
import { fetchContextMeaning } from '../../lib/contextMeaningApi.js';
import type { LexiconWord } from '../../lib/lexiconApi.js';
import {
  deleteVocabByText,
  describeSpeakError,
  saveVocab,
  speakText,
} from '../../lib/englishApi.js';
import { useAudioBlob } from '../../lib/audioBlobPlayer.js';
import { WordPopover } from '../../components/word-lookup/WordPopover.js';
import { resolveLookup } from '../../components/word-lookup/conversationResolver.js';
import { lookupEntryOf } from '../../components/word-lookup/meaningResolver.js';
import {
  normalizeWord,
  type MeaningLookup,
  type WordLookupEntry,
} from '../../components/word-lookup/types.js';

/** Cuántas fichas pueden quedar apiladas (la primera incluida). */
export const MAX_SHEET_DEPTH = 4;

/** Lo que abre una ficha desde un toque: la entrada, en qué significado y qué aclarar. */
export interface ContextMeaningOpen {
  entry: LexiconWord;
  meaningId: string | null;
  /** «No pudimos elegir el significado…»: la ficha lo dice arriba de los significados. */
  note: string | null;
}

/**
 * Qué ficha abre «Ver ficha» de la tarjeta: en el significado que se ve, o en
 * la palabra diciendo que no se pudo elegir. Lo comparten la charla y la ficha.
 */
export function contextMeaningOpenOf(
  m: MeaningLookup,
  word: string,
  sentence: string,
): ContextMeaningOpen | null {
  if (m.kind === 'picked') return { entry: m.entry, meaningId: m.meaningId, note: null };
  if (m.kind === 'unchosen')
    return {
      entry: m.entry,
      meaningId: null,
      note: `No pudimos elegir el significado de «${word}» en «${sentence}». Estos son todos sus significados.`,
    };
  return null;
}

/** Lo que dice la tarjeta en lugar de «Ver ficha» cuando ya hay {@link MAX_SHEET_DEPTH} fichas. */
export const SHEET_DEPTH_NOTICE = 'Hay muchas fichas abiertas: volvé atrás para abrir otra.';

/** Lo que la ficha necesita para que sus textos se puedan tocar. */
export interface SheetTapLookup {
  config: ContextMeaningPublicConfig;
  /** False cuando ya hay {@link MAX_SHEET_DEPTH} fichas: se ve el significado, pero no se abre otra. */
  canOpen: boolean;
  onOpen: (o: ContextMeaningOpen) => void;
  sessionId?: string;
}

interface TapTarget {
  origin: ContextMeaningOrigin;
  lang: 'en' | 'es';
  sentence: string;
  offset: number;
  word: string;
}

/** Dónde flota la tarjeta, en la pantalla (la capa `bf-tap-host` es fija y la cubre). */
interface TapAnchor {
  cx: number;
  top: number;
  /** Arriba de la palabra (abajo no entra y arriba hay más lugar). */
  flip: boolean;
  /** La flecha, corrida desde el centro de la tarjeta hasta la palabra. */
  arrowDx: number;
}

type TapState = { target: TapTarget; word: HTMLElement } & (
  | { status: 'loading' }
  | { status: 'ready'; entry: WordLookupEntry }
  | { status: 'error' }
);

/** Medio ancho de la tarjeta (`.word-pop`, 240 px) y su margen con el borde. */
const POP_HALF = 120;
const POP_PAD = 8;
/** Lo más que se corre la flecha: que no pise las esquinas redondeadas. */
const ARROW_MAX_DX = POP_HALF - 22;
/** Alto de la tarjeta antes de poder medirla (la «Con ejemplo» completa ronda esto). */
const POP_H = 400;
/** Lo que separa la tarjeta de la palabra (la flecha) y del borde de la pantalla. */
const POP_GAP = 8;

/**
 * La tarjeta, anclada a la palabra en coordenadas de pantalla. A lo ancho no
 * se sale de la ficha; a lo alto, de la pantalla de la app (`.bib-overlay`).
 * `keep`: el lado en que ya está, que se respeta mientras la tarjeta entre ahí.
 */
function anchorOf(word: HTMLElement, popH: number, keep?: boolean): TapAnchor {
  const w = word.getBoundingClientRect();
  const sheet = word.closest<HTMLElement>('.bib-sheet')?.getBoundingClientRect();
  const left = sheet ? sheet.left : 0;
  const width = sheet ? sheet.width : window.innerWidth;
  const raw = w.left + w.width / 2;
  const min = left + POP_HALF + POP_PAD;
  const max = left + width - POP_HALF - POP_PAD;
  const cx = max < min ? left + width / 2 : Math.max(min, Math.min(max, raw));
  const screen = word.closest<HTMLElement>('.bib-overlay')?.getBoundingClientRect();
  const top = screen ? screen.top : 0;
  const bottom = screen ? screen.bottom : window.innerHeight;
  const below = bottom - w.bottom - POP_GAP * 2;
  const above = w.top - top - POP_GAP * 2;
  const fits = (up: boolean): boolean => popH <= (up ? above : below);
  const flip = keep !== undefined && fits(keep) ? keep : popH > below && above > below;
  // La tarjeta se corre para no salirse del borde; la flecha no: sigue a la palabra.
  const arrowDx = Math.max(-ARROW_MAX_DX, Math.min(ARROW_MAX_DX, raw - cx));
  return { cx, top: flip ? w.top - 6 : w.bottom + POP_GAP, flip, arrowDx };
}

/** Si la palabra todavía se ve dentro del scroll de la ficha. */
function visibleInSheet(word: HTMLElement): boolean {
  const scroller = word.closest<HTMLElement>('.bib-sheet-scroll');
  if (!scroller) return true;
  const w = word.getBoundingClientRect();
  const b = scroller.getBoundingClientRect();
  // Sin alto (todavía sin dibujar) no hay nada que decidir.
  return b.height === 0 || (w.bottom > b.top && w.top < b.bottom);
}

const sameAnchor = (a: TapAnchor, b: TapAnchor): boolean =>
  a.cx === b.cx && a.top === b.top && a.flip === b.flip && a.arrowDx === b.arrowDx;

const WORD_CHARS = /[\p{L}'’-]/u;

/** Los pedazos de un texto: palabras tocables y lo que va entre ellas. */
function pieces(text: string): { text: string; start: number; word: boolean }[] {
  const out: { text: string; start: number; word: boolean }[] = [];
  const re = /\S+/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) out.push({ text: text.slice(last, m.index), start: last, word: false });
    // La puntuación de los bordes no es parte de lo que se toca.
    const raw = m[0];
    let a = 0;
    let b = raw.length;
    while (a < b && !WORD_CHARS.test(raw[a]!)) a++;
    while (b > a && !WORD_CHARS.test(raw[b - 1]!)) b--;
    if (a > 0) out.push({ text: raw.slice(0, a), start: m.index, word: false });
    if (b > a)
      out.push({ text: raw.slice(a, b), start: m.index + a, word: /\p{L}/u.test(raw.slice(a, b)) });
    if (b < raw.length) out.push({ text: raw.slice(b), start: m.index + b, word: false });
    last = m.index + raw.length;
  }
  if (last < text.length) out.push({ text: text.slice(last), start: last, word: false });
  return out;
}

/**
 * Un texto de la ficha con sus palabras tocables. `mark` resalta un tramo (la
 * palabra del ejemplo); `active` es la posición tocada, que queda marcada.
 */
export function TapText({
  text,
  origin,
  lang,
  mark,
  active,
  onTap,
}: {
  text: string;
  origin: ContextMeaningOrigin;
  lang: 'en' | 'es';
  mark?: { start: number; end: number } | null;
  active: TapTarget | null;
  onTap: (t: TapTarget, el: HTMLElement) => void;
}) {
  return (
    <>
      {pieces(text).map((p) => {
        const marked = mark != null && p.start >= mark.start && p.start + p.text.length <= mark.end;
        if (!p.word) {
          const plain = <Fragment key={p.start}>{p.text}</Fragment>;
          return marked ? <mark key={p.start}>{p.text}</mark> : plain;
        }
        const on =
          active?.origin === origin && active.sentence === text && active.offset === p.start;
        const target: TapTarget = { origin, lang, sentence: text, offset: p.start, word: p.text };
        const word = (
          <span
            key={p.start}
            role="button"
            tabIndex={0}
            className={`bf-tap ${on ? 'on' : ''}`}
            onClick={(e) => onTap(target, e.currentTarget)}
            onKeyDown={(e: KeyboardEvent<HTMLElement>) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTap(target, e.currentTarget);
              }
            }}
          >
            {p.text}
          </span>
        );
        return marked ? <mark key={p.start}>{word}</mark> : word;
      })}
    </>
  );
}

/** Lo tocado y su resultado; `resetKey` lo limpia (otra palabra u otro significado en la ficha). */
/** `sourceRef`: el significado (o la palabra) de la ficha donde se toca: el origen que lee la #409. */
export function useSheetTap(lookup: SheetTapLookup | undefined, resetKey: string, sourceRef?: string) {
  const [state, setState] = useState<TapState | null>(null);
  const req = useRef(0);

  useEffect(() => {
    req.current++;
    setState(null);
  }, [resetKey]);

  const onTap = useCallback(
    (target: TapTarget, el: HTMLElement) => {
      if (!lookup) return;
      const id = ++req.current;
      setState({ target, word: el, status: 'loading' });
      void (async () => {
        try {
          const result = await fetchContextMeaning({
            text: target.word,
            sentence: target.sentence,
            offset: target.offset,
            origin: target.origin,
            lang: target.lang,
            ...(lookup.sessionId ? { sessionId: lookup.sessionId } : {}),
            ...(sourceRef ? { sourceRef } : {}),
          });
          const missingWord =
            result.status === 'not-in-dictionary' || result.status === 'no-meanings';
          // La traducción de hoy, rotulada como automática: la misma que la charla.
          const auto =
            missingWord && lookup.config.missingWordDisplay === 'auto-translation'
              ? await resolveLookup(target.word, target.sentence, lookup.sessionId).catch(
                  () => null,
                )
              : null;
          // Un significado que no está en su entrada tira: se ve como error, no como otra cosa.
          const entry = lookupEntryOf(result, auto);
          if (id === req.current) setState({ target, word: el, status: 'ready', entry });
        } catch {
          if (id === req.current) setState({ target, word: el, status: 'error' });
        }
      })();
    },
    [lookup, sourceRef],
  );

  const close = useCallback(() => {
    req.current++;
    setState(null);
  }, []);

  // Como en la charla: tocar afuera (salvo otra palabra, que la cambia) o Escape la cierra.
  const open = state !== null;
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: Event): void => {
      const t = e.target as Element | null;
      if (t?.closest?.('.word-pop, .bf-tap')) return;
      close();
    };
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Guardado desde la tarjeta, por palabra: tocarla otra vez en la ficha lo recuerda.
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  // Fuera de la ficha, sobre toda la pantalla: no la estira ni la scrollea. `bib-root`
  // trae los tokens de la Biblioteca, que en `document.body` no están.
  const box: ReactNode =
    lookup && state
      ? createPortal(
          <div className="bib-root bf-tap-host">
            <SheetMeaningCard
              state={state}
              lookup={lookup}
              saved={!!saved[normalizeWord(state.target.word)]}
              onSaved={(word, on) => setSaved((p) => ({ ...p, [normalizeWord(word)]: on }))}
              onClose={close}
            />
          </div>,
          document.body,
        )
      : null;
  return { active: state?.target ?? null, onTap, box };
}

/** El resultado del toque: la tarjeta «Con ejemplo» de la charla, flotando sobre la palabra. */
function SheetMeaningCard({
  state,
  lookup,
  saved,
  onSaved,
  onClose,
}: {
  state: TapState;
  lookup: SheetTapLookup;
  saved: boolean;
  onSaved: (word: string, on: boolean) => void;
  onClose: () => void;
}) {
  const { word, sentence } = state.target;
  const entry = state.status === 'ready' ? state.entry : null;
  const audio = useAudioBlob();
  const ref = useRef<HTMLDivElement | null>(null);
  const opts = lookup.sessionId ? { sessionId: lookup.sessionId } : {};
  const el = state.word;
  const [anchor, setAnchor] = useState<TapAnchor>(() => anchorOf(el, POP_H));
  // Mientras busca, la tarjeta es chica: el lado se elige por la que va a llegar,
  // para que no salte de abajo a arriba cuando aparece el significado.
  const loading = useRef(state.status === 'loading');
  loading.current = state.status === 'loading';

  // Pegada a la palabra: se mide con su alto real (crece al llegar el
  // significado) y se re-ubica si la ficha scrollea o la pantalla cambia. Si la
  // palabra sale de la vista de la ficha, la tarjeta se cierra con ella.
  useLayoutEffect(() => {
    const place = (): void => {
      if (!el.isConnected || !visibleInSheet(el)) {
        onClose();
        return;
      }
      const measured = ref.current?.querySelector<HTMLElement>('.word-pop')?.offsetHeight ?? 0;
      const h = loading.current || !measured ? Math.max(measured, POP_H) : measured;
      setAnchor((prev) => {
        const next = anchorOf(el, h, prev.flip);
        return sameAnchor(prev, next) ? prev : next;
      });
    };
    place();
    const pop = ref.current?.querySelector<HTMLElement>('.word-pop');
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
    if (pop) ro?.observe(pop);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      ro?.disconnect();
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [el, onClose]);

  const toggleSave = (translation?: string): void => {
    const next = !saved;
    onSaved(word, next);
    const revert = () => onSaved(word, !next);
    if (next)
      void saveVocab({ word, ...(translation ? { translation } : {}), ...opts }).catch(revert);
    else void deleteVocabByText({ text: word }).catch(revert);
  };

  return (
    <div ref={ref}>
      <WordPopover
        isPhrase={false}
        word={word}
        phraseText=""
        phraseWordCount={0}
        wordEntry={entry}
        wordLoading={state.status === 'loading'}
        phraseInfo={null}
        anchor={{ cx: anchor.cx, top: anchor.top }}
        flip={anchor.flip}
        arrowDx={anchor.arrowDx}
        saved={saved}
        playing={audio.state === 'playing'}
        playError={audio.state === 'error' ? describeSpeakError(audio.error) : null}
        enablePhrase={false}
        onOpenMeaning={(m) => {
          const open = contextMeaningOpenOf(m, word, sentence);
          if (!open) return;
          // Como en la charla: la tarjeta se cierra y la ficha se abre encima.
          onClose();
          lookup.onOpen(open);
        }}
        openNotice={lookup.canOpen ? null : SHEET_DEPTH_NOTICE}
        sentence={sentence}
        onConfirmPhrase={() => undefined}
        onPlay={() => void audio.toggle(() => speakText(word, opts))}
        onSave={toggleSave}
        onClose={onClose}
      />
    </div>
  );
}

